import { StreamSettingResponse } from "@switcherstudio/api-core-client";
import { SwitcherStreamSetting } from "@switcherstudio/switcher-api-client";
import { parse } from "helpers/json";
import { getChannelName } from "helpers/streamSettings";
import {
    TwitchMetadata,
    YoutubeMetadata
} from "views/page-content/VideoDetailsPage/ExternalDestinations/types";
import { DestinationDescriptor } from "views/page-content/platforms/platform/simulcast/types";
import { PlatformId } from "views/page-content/platforms/types";

// Mirrors the default filteredDestinations in ExternalDestinations.
export const IGNORED_STREAM_SETTING_DISCRIMINATORS = [
    "VideoPlayerSwitcherStreamSetting",
    "SimulcastSwitcherStreamSetting"
];

// Anything that isn't Facebook/YouTube/Twitch falls back to plain RTMP.
const brandedSingleUseProviderIds: string[] = [
    PlatformId.AmazonLive,
    PlatformId.Caast,
    PlatformId.Instafeed,
    PlatformId.MsStream
];

const safeParseMeta = <T extends object>(meta: string | undefined): T | null => {
    if (!meta) return null;
    try {
        return parse<T>(meta);
    } catch {
        return null;
    }
};

// The RTMP/branded stream setting attached to a broadcast is a POST-ed copy
// of the user's original channel (sc-56801 FE-4 FIX 4) - it doesn't share an
// id with the original, so resolve the original by matching url + stream key
// against the user's editable channels.
const resolveOriginalRtmpChannelId = (
    details: StreamSettingResponse["Details"],
    userEditableRtmpChannels: SwitcherStreamSetting[] | undefined
): string | undefined => {
    const match = userEditableRtmpChannels?.find(
        (c) =>
            c.url === details.RtmpStreamUrl &&
            c["rtmp-stream"] === details.RtmpStream
    );
    return match?.["switcher-stream-setting-id"] as string | undefined;
};

const mapStreamSettingToDestinationDescriptor = (
    setting: StreamSettingResponse,
    userEditableRtmpChannels: SwitcherStreamSetting[] | undefined
): DestinationDescriptor | null => {
    const details = setting?.Details;
    if (!details) return null;

    switch (details.Discriminator) {
        case "FaceBookSwitcherStreamSetting":
            return {
                id: details.FacebookEdgeId,
                platform: PlatformId.Facebook,
                name: getChannelName(setting)
            };
        case "YouTubeSwitcherStreamSetting": {
            const channelId = safeParseMeta<YoutubeMetadata>(details.Meta)
                ?.userData?.id;
            return {
                id: channelId || details.SwitcherStreamSettingId,
                platform: PlatformId.Youtube,
                name: getChannelName(setting)
            };
        }
        case "TwitchSwitcherStreamSetting": {
            const broadcasterId = safeParseMeta<TwitchMetadata>(details.Meta)
                ?.broadcasterId;
            return {
                id: broadcasterId || details.SwitcherStreamSettingId,
                platform: PlatformId.Twitch,
                name: getChannelName(setting)
            };
        }
        default: {
            const platform = brandedSingleUseProviderIds.includes(
                details.StreamingProviderId
            )
                ? (details.StreamingProviderId as PlatformId)
                : PlatformId.Rtmp;

            const originalId = resolveOriginalRtmpChannelId(
                details,
                userEditableRtmpChannels
            );

            return {
                id: originalId || details.SwitcherStreamSettingId,
                platform,
                name: getChannelName(setting)
            };
        }
    }
};

// Builds the set of already-connected destinations for a broadcast, used to
// prevent duplicates when adding more destinations to a scheduled livestream.
export const getConnectedDestinationDescriptors = (
    streamSettings: StreamSettingResponse[] | undefined,
    userEditableRtmpChannels?: SwitcherStreamSetting[]
): DestinationDescriptor[] => {
    if (!streamSettings?.length) return [];

    return streamSettings
        .filter(
            (s) =>
                !IGNORED_STREAM_SETTING_DISCRIMINATORS.includes(
                    s.Details?.Discriminator
                )
        )
        .map((s) =>
            mapStreamSettingToDestinationDescriptor(s, userEditableRtmpChannels)
        )
        .filter((d): d is DestinationDescriptor => !!d?.id);
};
